import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { usePreferredDark } from '@vueuse/core'
import type { RouteLocationNormalized } from 'vue-router'

export type ThemeMode = 'light' | 'dark' | 'auto'
export type ComponentSize = 'large' | 'default' | 'small'
export type LocaleKey = 'zh-CN' | 'en-US'

export interface TagView {
  /** 路由 path，作为唯一键 */
  path: string
  fullPath: string
  /** i18n key */
  titleKey: string
  name?: string
  affix?: boolean
  /** 不参与 keep-alive 缓存（详情页等带参数的页面） */
  noCache?: boolean
}

/**
 * 应用状态：布局、主题、多页签、语言
 */
export const useAppStore = defineStore(
  'app',
  () => {
    const systemDark = usePreferredDark()

    const sidebarCollapsed = ref(false)
    const theme = ref<ThemeMode>('light')
    const language = ref<LocaleKey>('zh-CN')
    const componentSize = ref<ComponentSize>('default')
    const device = ref<'desktop' | 'mobile'>('desktop')
    const tags = ref<TagView[]>([])

    /** 实际是否为深色（auto 时跟随系统） */
    const isDark = computed(() =>
      theme.value === 'auto' ? systemDark.value : theme.value === 'dark'
    )

    function applyThemeClass(): void {
      const root = document.documentElement
      if (isDark.value) root.classList.add('dark')
      else root.classList.remove('dark')
    }

    watch(isDark, applyThemeClass, { immediate: true })

    function toggleSidebar(): void {
      sidebarCollapsed.value = !sidebarCollapsed.value
    }

    function setTheme(mode: ThemeMode): void {
      theme.value = mode
      applyThemeClass()
    }

    function setLanguage(lang: LocaleKey): void {
      language.value = lang
    }

    function setSize(size: ComponentSize): void {
      componentSize.value = size
    }

    function setDevice(width: number): void {
      device.value = width < 992 ? 'mobile' : 'desktop'
      if (device.value === 'mobile') sidebarCollapsed.value = true
    }

    /** 多页签 */
    function addTag(route: RouteLocationNormalized): void {
      if (route.meta?.hidden) return
      const titleKey = (route.meta?.titleKey as string) || 'route.unknown'
      const existing = tags.value.find((item) => item.path === route.path)
      if (existing) {
        existing.fullPath = route.fullPath
        return
      }
      tags.value.push({
        path: route.path,
        fullPath: route.fullPath,
        titleKey,
        name: typeof route.name === 'string' ? route.name : undefined,
        affix: Boolean(route.meta?.affix),
        noCache: Boolean(route.meta?.noCache),
      })
    }

    function removeTag(path: string): TagView[] {
      const index = tags.value.findIndex((item) => item.path === path)
      if (index > -1 && !tags.value[index]?.affix) tags.value.splice(index, 1)
      return tags.value
    }

    function removeOtherTags(path: string): void {
      tags.value = tags.value.filter((item) => item.affix || item.path === path)
    }

    function removeAllTags(): void {
      tags.value = tags.value.filter((item) => item.affix)
    }

    /** 需要 keep-alive 缓存的组件名（与路由 name 一致） */
    const cachedViews = computed<string[]>(() =>
      tags.value.filter((item) => item.name && !item.noCache).map((item) => item.name as string)
    )

    return {
      sidebarCollapsed,
      theme,
      language,
      componentSize,
      device,
      tags,
      cachedViews,
      isDark,
      toggleSidebar,
      setTheme,
      setLanguage,
      setSize,
      setDevice,
      addTag,
      removeTag,
      removeOtherTags,
      removeAllTags,
    }
  },
  {
    persist: {
      key: 'wordapp-app',
      storage: localStorage,
      pick: ['sidebarCollapsed', 'theme', 'language', 'componentSize'],
    },
  }
)
